// ---------------------------------------------------------------------------
// QueueRunDialog — shown when the user queues a hosted evaluation run. It lists
// the template's overridable variables (merged from the referenced agent(s),
// grader(s), and the template itself; see useTemplateRunVariables) prefilled
// with their declared defaults, and lets the user override any of them for
// this run only.
//
// Overriding here does NOT edit the template/agent/grader or create a new
// version — the values ride along as the run's `variables` and are applied on
// top of the resolved snapshot's defaults at execution time. Leaving a field at
// its default sends nothing for it, so the run uses the declared default.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useEffect, useState } from 'react';

import type { RunVariable } from './useTemplateRunVariables.js';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalS,
  },
  spinnerWrap: {
    paddingBlock: tokens.spacingVerticalM,
  },
});

export interface QueueRunDialogProps {
  open: boolean;
  /** Resolved overridable variables (merged agent + grader + template). */
  variables: RunVariable[];
  /** True while the variables are still being resolved. */
  variablesLoading: boolean;
  /** Non-fatal resolution error (some references failed to load). */
  variablesError?: string | null;
  /** True while the queue (and optional snapshot) request is in flight. */
  queueing: boolean;
  /** Optional note rendered above the parameters, e.g. the snapshot warning. */
  note?: string | null;
  /** Primary button label, e.g. "Queue job" or "Snapshot & queue". */
  confirmLabel: string;
  onOpenChange: (open: boolean) => void;
  /** Confirm the run with the collected per-run overrides (may be empty). */
  onConfirm: (variables: Record<string, unknown>) => void;
}

function defaultToString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

// Coerces boolean/string overrides. Numeric types (number/integer) are parsed
// and validated inline in handleConfirm (empty/invalid input is rejected there
// rather than silently becoming 0/NaN), so they never reach this helper.
function coerce(type: string, raw: string): unknown {
  if (type.toLowerCase() === 'boolean') return raw === 'true';
  return raw;
}

export function QueueRunDialog({
  open,
  variables,
  variablesLoading: isLoading,
  variablesError: error,
  queueing,
  note,
  confirmLabel,
  onOpenChange,
  onConfirm,
}: QueueRunDialogProps) {
  const styles = useStyles();

  // Sparse map of user edits keyed by variable name — a field with no entry
  // shows (and submits) its declared default. Crucially we do NOT re-seed this
  // from `variables`, so a late/re-resolved variable set can never clobber a
  // value the user just typed. We only clear edits when the dialog re-opens.
  const [values, setValues] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setValues({});
      setValidationError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    setValidationError(null);

    const overrides: Record<string, unknown> = {};
    for (const v of variables) {
      const edited = values[v.name];
      // Untouched field → omit, so the run uses the declared default.
      if (edited === undefined) continue;
      // Cleared input → treat as removing the override (restore the default),
      // rather than coercing '' into 0/NaN.
      if (edited.trim() === '') continue;
      // Typed back to the default → also omit.
      if (edited === defaultToString(v.default)) continue;

      const type = v.type.toLowerCase();
      if (type === 'number' || type === 'integer') {
        // Number() (not parseInt) so partial/garbage input like '1.2.3' or
        // 'abc' fails validation instead of being silently truncated.
        const n = Number(edited);
        if (!Number.isFinite(n)) {
          setValidationError(`"${v.name}" must be a valid ${type}.`);
          return;
        }
        if (type === 'integer' && !Number.isInteger(n)) {
          setValidationError(`"${v.name}" must be a whole number.`);
          return;
        }
        overrides[v.name] = n;
        continue;
      }

      overrides[v.name] = coerce(v.type, edited);
    }
    onConfirm(overrides);
  };

  return (
    <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Queue evaluation run</DialogTitle>
          <DialogContent>
            {note !== null && note !== undefined && note.length > 0 && (
              <Body1 className={styles.hint}>{note}</Body1>
            )}

            {isLoading ? (
              <div className={styles.spinnerWrap}>
                <Spinner size="tiny" label="Loading parameters…" />
              </div>
            ) : variables.length === 0 ? (
              <Body1 className={styles.hint}>
                This template has no overridable parameters. It will run with
                its saved configuration.
              </Body1>
            ) : (
              <div className={styles.list}>
                <Caption1 className={styles.hint}>
                  Overrides apply to this run only — they don&apos;t change the
                  template, agent, or grader, or create a new version.
                </Caption1>
                {variables.map((v) => (
                  <QueueRunVariableField
                    key={v.name}
                    variable={v}
                    value={values[v.name] ?? defaultToString(v.default)}
                    disabled={queueing}
                    onChange={(next) =>
                      setValues((prev) => ({ ...prev, [v.name]: next }))
                    }
                  />
                ))}
              </div>
            )}

            {validationError !== null && (
              <Body1 className={styles.errorText} role="alert">
                {validationError}
              </Body1>
            )}
            {error !== null && error !== undefined && (
              <Body1 className={styles.errorText} role="alert">
                {error}
              </Body1>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => onOpenChange(false)}
              disabled={queueing}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={queueing || isLoading}
            >
              {queueing ? 'Queueing…' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface QueueRunVariableFieldProps {
  variable: RunVariable;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}

function QueueRunVariableField({
  variable,
  value,
  disabled,
  onChange,
}: QueueRunVariableFieldProps) {
  const styles = useStyles();
  const type = variable.type.toLowerCase();
  const hint =
    (variable.description ? `${variable.description} · ` : '') +
    `from ${variable.source} · default: ${defaultToString(variable.default) || '(none)'}`;

  return (
    <Field
      label={variable.name}
      hint={<span className={styles.hint}>{hint}</span>}
    >
      {type === 'boolean' ? (
        <Dropdown
          aria-label={variable.name}
          value={value}
          selectedOptions={[value]}
          disabled={disabled}
          onOptionSelect={(_e, data) => {
            if (data.optionValue !== undefined) onChange(data.optionValue);
          }}
        >
          <Option value="true" text="true">
            true
          </Option>
          <Option value="false" text="false">
            false
          </Option>
        </Dropdown>
      ) : (
        <Input
          aria-label={variable.name}
          type={type === 'number' || type === 'integer' ? 'number' : 'text'}
          value={value}
          disabled={disabled}
          onChange={(_e, data) => onChange(data.value)}
        />
      )}
    </Field>
  );
}
